import { useConfigStore } from "@store/index";
import type { LensesObject, CartMode } from "@/types/api.types";
import type { WidgetConfig } from "@/types/config.types";

/**
 * Custom hook for managing actions module operations
 * All operations close the widget after execution
 */
export const useActionsModule = () => {
  const { config } = useConfigStore();

  /**
   * Closes the widget application
   */
  const closeWidget = () => {
    window.__clWidgetInstance?.close();
  };

  /**
   * Generic add to cart - adds contact lenses to cart
   * @param lensesObject - The lenses object to add to cart
   */
  const genericAddToCart = async (
    lensesObject: LensesObject,
  ): Promise<void> => {
    try {
      if (config?.actionsModule?.genericAddToCart) {
        await config.actionsModule.genericAddToCart(lensesObject);
      } else {
        console.warn("genericAddToCart not configured");
      }
    } catch (error) {
      console.error("Error in genericAddToCart:", error);
      throw error;
    }
  };

  /**
   * Generic save/edit from cart - saves or edits contact lenses in cart
   * @param lensesObject - The lenses object to save/edit
   * @param cartMode - The cart mode with order information
   */
  const genericSaveEditFromCart = async (
    lensesObject: LensesObject,
    cartMode: CartMode,
  ): Promise<void> => {
    try {
      if (config?.actionsModule?.genericSaveEditFromCart) {
        await config.actionsModule.genericSaveEditFromCart(
          lensesObject,
          cartMode,
        );
      } else {
        console.warn("genericSaveEditFromCart not configured");
      }
    } catch (error) {
      console.error("Error in genericSaveEditFromCart:", error);
      throw error;
    }
  };

  /**
   * Go back to PLP (Product Listing Page)
   * @param lensesObject - The lenses object
   * @param widgetConfig - The widget configuration
   */
  const goBackToPLP = (
    lensesObject: LensesObject | null,
    cartMode: CartMode,
    widgetConfig: WidgetConfig,
  ): void => {
    try {
      if (config?.actionsModule?.goBackToPLP) {
        config.actionsModule.goBackToPLP(lensesObject, cartMode, widgetConfig);
      } else {
        console.warn("goBackToPLP not configured");
      }
    } catch (error) {
      console.error("Error in goBackToPLP:", error);
      throw error;
    }
  };

  const removeProduct = (
    orderId: string,
    orderProductId: string,
    isFromCart: boolean,
    eye: "OD" | "OS",
  ): void => {
    try {
      if (config?.actionsModule?.removeProduct) {
        config.actionsModule.removeProduct(
          orderId,
          orderProductId,
          isFromCart,
          eye,
        );
      } else {
        console.warn("removeProduct not configured");
      }
    } catch (error) {
      console.error("Error in removeProduct:", error);
      throw error;
    }
  };

  return {
    genericAddToCart,
    genericSaveEditFromCart,
    goBackToPLP,
    removeProduct,
    closeWidget,
  };
};
